import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { ESTADO_NAMES } from '../lib/filters';
import type { PlaceSuggestion } from '../lib/types';

interface Props {
  /** Texto del input (controlado por el padre). */
  value: string;
  /** Cambios debounced del search libre (texto sin selección). */
  onSearch: (text: string) => void;
  /** Selección de un lugar concreto del dropdown. */
  onSelectPlace: (p: PlaceSuggestion) => void;
  placeholder?: string;
}

const SEARCH_DEBOUNCE_MS = 200;
const MIN_CHARS = 2;

/**
 * Combobox de lugares (estado / municipio / colonia) sobre /v1/places.
 *
 * Comportamiento clave:
 * - Si el user TIPEA y luego hace blur sin seleccionar nada del dropdown,
 *   el texto se trata como búsqueda libre (full-text vía `q` filter).
 * - Si SELECCIONA un item del dropdown, se dispara `onSelectPlace` con
 *   `bbox`/`center`/`cveEnt`/`cveMun` para que el padre haga flyTo y
 *   aplique los filtros estructurados que correspondan.
 * - Navegación con flechas + Enter es totalmente teclado-friendly
 *   (a11y: combobox role + aria-activedescendant).
 *
 * No bloqueamos la búsqueda libre cuando hay sugerencias: el AC pide
 * que ambos coexistan ("autocomplete + búsqueda full-text"), y eso es
 * lo que la mayoría de productos modernos (Zillow, Redfin, Idealista)
 * hacen — escribir libremente filtra, seleccionar del dropdown navega.
 */
export function LocationAutocomplete({ value, onSearch, onSelectPlace, placeholder }: Props) {
  const [items, setItems] = useState<PlaceSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<number | undefined>(undefined);
  const ctrlRef = useRef<AbortController | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  // Fetch suggestions cuando cambia value (debounced).
  useEffect(() => {
    window.clearTimeout(debounceRef.current);
    ctrlRef.current?.abort();

    const term = value.trim();
    if (term.length < MIN_CHARS) {
      setItems([]);
      setLoading(false);
      return;
    }

    debounceRef.current = window.setTimeout(() => {
      const ctrl = new AbortController();
      ctrlRef.current = ctrl;
      setLoading(true);
      api
        .places(term, { limit: 10, signal: ctrl.signal })
        .then((r) => {
          setItems(r.items);
          setHighlight(r.items.length > 0 ? 0 : -1);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(debounceRef.current);
  }, [value]);

  // Al desmontar: aborta el request de places en vuelo (el cleanup de
  // arriba solo limpia el timer).
  useEffect(() => () => ctrlRef.current?.abort(), []);

  // Cierra el dropdown al click fuera del wrapper.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function commitSearch(text: string) {
    onSearch(text.trim());
    setOpen(false);
  }

  function selectItem(item: PlaceSuggestion) {
    onSelectPlace(item);
    setOpen(false);
    inputRef.current?.blur();
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (h + 1 >= items.length ? 0 : h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? items.length - 1 : h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && highlight >= 0 && items[highlight]) {
        selectItem(items[highlight]);
      } else {
        commitSearch(value);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  const showDropdown = open && (loading || items.length > 0) && value.trim().length >= MIN_CHARS;
  const activeId = highlight >= 0 ? `${listboxId}-opt-${highlight}` : undefined;

  return (
    <div className="search-wrap loc-autocomplete" ref={wrapRef}>
      <span className="search-icon" aria-hidden="true">⌕</span>
      <input
        ref={inputRef}
        type="search"
        className="search-input"
        value={value}
        onChange={(e) => { onSearch(e.target.value); setOpen(true); }}
        onFocus={() => { if (items.length > 0) setOpen(true); }}
        onKeyDown={handleKey}
        placeholder={placeholder ?? 'Buscar estado, municipio o colonia…'}
        autoComplete="off"
        spellCheck={false}
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={activeId}
      />
      {value && (
        <button
          className="search-clear"
          onClick={() => { onSearch(''); setOpen(false); inputRef.current?.focus(); }}
          aria-label="Limpiar búsqueda"
          title="Limpiar"
        >×</button>
      )}
      {showDropdown && (
        <ul
          className="loc-list"
          role="listbox"
          id={listboxId}
        >
          {loading && items.length === 0 && (
            <li className="loc-item is-loading" aria-disabled>buscando…</li>
          )}
          {items.map((item, i) => (
            <Suggestion
              key={`${item.kind}-${item.id}`}
              item={item}
              highlighted={i === highlight}
              id={`${listboxId}-opt-${i}`}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => selectItem(item)}
            />
          ))}
          {!loading && items.length === 0 && value.trim().length >= MIN_CHARS && (
            <li className="loc-item is-empty" aria-disabled>
              sin coincidencias — tu texto se buscará en direcciones
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function Suggestion({ item, highlighted, id, onMouseEnter, onClick }: {
  item: PlaceSuggestion;
  highlighted: boolean;
  id: string;
  onMouseEnter: () => void;
  onClick: () => void;
}) {
  const subtitle = useMemo(() => buildSubtitle(item), [item]);
  return (
    <li
      role="option"
      id={id}
      aria-selected={highlighted}
      className={`loc-item ${highlighted ? 'is-highlight' : ''}`}
      onMouseEnter={onMouseEnter}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    >
      <span className={`loc-kind kind-${item.kind}`}>
        {KIND_LABEL[item.kind]}
      </span>
      <span className="loc-name">{item.name}</span>
      <span className="loc-meta">
        {subtitle}
        {item.count > 0 && (
          <span className="loc-count"> · {item.count.toLocaleString('es-MX')}</span>
        )}
      </span>
    </li>
  );
}

const KIND_LABEL: Record<PlaceSuggestion['kind'], string> = {
  estado: 'Estado',
  municipio: 'Municipio',
  colonia: 'Colonia',
};

function buildSubtitle(item: PlaceSuggestion): string {
  if (item.kind === 'estado') return 'Entidad federativa';
  if (item.cveEnt) return ESTADO_NAMES[item.cveEnt] ?? `CVE ${item.cveEnt}`;
  return '';
}
